import type { JsonObject, JsonValue } from '../../common.types';
import {
    CloverRefundKind,
    type CloverLifecycleFinalizationGatewayOperation,
    type CloverLifecyclePreflightGatewayOperation,
    type CloverVoidReason,
    type CloverTerminalSaleResponseLifecyclePayload,
    type PaymentGatewayTerminalSaleResult,
} from './gateway-lifecycle-request.types';

export const CloverTransactionDetailsPaymentResult = {
    SUCCESS: 'SUCCESS',
    FAIL: 'FAIL',
    INITIATED: 'INITIATED',
    VOIDED: 'VOIDED',
    VOIDING: 'VOIDING',
    VOID_FAILED: 'VOID_FAILED',
    AUTH: 'AUTH',
    AUTH_COMPLETED: 'AUTH_COMPLETED',
    DISCOUNT: 'DISCOUNT',
} as const;

export type CloverTransactionDetailsPaymentResult =
    (typeof CloverTransactionDetailsPaymentResult)[keyof typeof CloverTransactionDetailsPaymentResult];

export interface CloverTransactionDetailsCardTransactionBody {
    readonly authCode?: string;
    readonly cardType?: string;
    readonly cardholderName?: string;
    readonly last4?: string;
    readonly referenceId?: string;
    readonly state?: string;
    readonly token?: string;
    readonly transactionNo?: string;
    readonly type?: string;
}

export interface CloverTransactionDetailsTenderBody {
    readonly id?: string;
    readonly label?: string;
    readonly labelKey?: string;
}

export interface CloverTransactionDetailsRefundBody {
    readonly id?: string;
    readonly amount?: number;
    readonly createdTime?: number;
    readonly externalReferenceId?: string;
    readonly payment?: { readonly id?: string };
    readonly taxAmount?: number;
    readonly tipAmount?: number;
    readonly voided?: boolean;
}

export interface CloverTransactionDetailsPaymentBody {
    readonly id: string;
    readonly amount?: number;
    readonly result?: CloverTransactionDetailsPaymentResult;
    readonly cardTransaction?: CloverTransactionDetailsCardTransactionBody;
    readonly createdTime?: number;
    readonly externalPaymentId?: string;
    readonly externalReferenceId?: string;
    readonly offline?: boolean;
    readonly refunds?: readonly CloverTransactionDetailsRefundBody[];
    readonly taxAmount?: number;
    readonly tipAmount?: number;
    readonly tender?: CloverTransactionDetailsTenderBody;
    readonly voided?: boolean;
}

export const CloverCnpTokenSaleStatus = {
    SUCCEEDED: 'succeeded',
    PENDING: 'pending',
    FAILED: 'failed',
} as const;

export type CloverCnpTokenSaleStatus =
    (typeof CloverCnpTokenSaleStatus)[keyof typeof CloverCnpTokenSaleStatus];

export const CloverCnpTokenSaleNetworkStatus = {
    APPROVED_BY_NETWORK: 'approved_by_network',
    DECLINED_BY_NETWORK: 'declined_by_network',
    NOT_SENT_TO_NETWORK: 'not_sent_to_network',
    REVERSED_AFTER_APPROVAL: 'reversed_after_approval',
} as const;

export type CloverCnpTokenSaleNetworkStatus =
    (typeof CloverCnpTokenSaleNetworkStatus)[keyof typeof CloverCnpTokenSaleNetworkStatus];

export const CloverCnpTokenSaleOutcomeType = {
    AUTHORIZED: 'authorized',
    BLOCKED: 'blocked',
    INVALID: 'invalid',
    ISSUER_DECLINED: 'issuer_declined',
    MANUAL_REVIEW: 'manual_review',
} as const;

export type CloverCnpTokenSaleOutcomeType =
    (typeof CloverCnpTokenSaleOutcomeType)[keyof typeof CloverCnpTokenSaleOutcomeType];

export interface CloverCnpTokenSaleChargeOutcomeBody {
    readonly network_status?: CloverCnpTokenSaleNetworkStatus;
    readonly reason?: string | null;
    readonly seller_message?: string;
    readonly type?: CloverCnpTokenSaleOutcomeType;
}

export interface CloverCnpTokenSaleSourceBody {
    readonly id?: string;
    readonly object?: string;
    readonly brand?: string | null;
    readonly exp_month?: string;
    readonly exp_year?: string;
    readonly first6?: string;
    readonly funding?: string;
    readonly last4?: string;
    readonly name?: string;
}

export interface CloverCnpTokenSaleChargeBody {
    readonly id: string;
    readonly amount: number;
    readonly amount_refunded?: number;
    readonly auth_code?: string | null;
    readonly captured: boolean;
    readonly created?: number;
    readonly currency: string;
    readonly description?: string | null;
    readonly external_reference_id?: string;
    readonly failure_code?: string | null;
    readonly failure_message?: string | null;
    readonly outcome?: CloverCnpTokenSaleChargeOutcomeBody;
    readonly paid: boolean;
    readonly receipt_email?: string | null;
    readonly ref_num?: string | null;
    readonly refunded?: boolean;
    readonly source?: CloverCnpTokenSaleSourceBody;
    readonly status: CloverCnpTokenSaleStatus;
}

export const CloverRefundOutcome = {
    REFUNDED: 'refunded',
    REJECTED: 'rejected',
    MALFORMED_RESPONSE: 'malformed_response',
} as const;

export type CloverRefundOutcome =
    (typeof CloverRefundOutcome)[keyof typeof CloverRefundOutcome];

export const CloverRefundPaymentResult = {
    SUCCESS: 'SUCCESS',
    FAIL: 'FAIL',
    INITIATED: 'INITIATED',
    VOIDED: 'VOIDED',
    VOIDING: 'VOIDING',
    VOID_FAILED: 'VOID_FAILED',
    AUTH: 'AUTH',
    AUTH_COMPLETED: 'AUTH_COMPLETED',
    DISCOUNT: 'DISCOUNT',
} as const;

export type CloverRefundPaymentResult =
    (typeof CloverRefundPaymentResult)[keyof typeof CloverRefundPaymentResult];

export interface CloverRefundReferenceBody {
    readonly id?: string;
}

export interface CloverRefundCardTransactionBody {
    readonly authCode?: string;
    readonly referenceId?: string;
    readonly state?: string;
    readonly token?: string;
    readonly transactionNo?: string;
    readonly type?: string;
}

export interface CloverRefundPaymentBody {
    readonly id?: string;
    readonly amount?: number;
    readonly result?: CloverRefundPaymentResult;
    readonly cardTransaction?: CloverRefundCardTransactionBody;
    readonly externalPaymentId?: string;
    readonly externalReferenceId?: string;
    readonly refunds?: readonly CloverRefundBody[];
}

export interface CloverRefundBody {
    readonly id?: string;
    readonly amount?: number;
    readonly cardTransaction?: CloverRefundCardTransactionBody;
    readonly createdTime?: number;
    readonly externalReferenceId?: string;
    readonly payment?: CloverRefundReferenceBody;
    readonly taxAmount?: number;
    readonly tipAmount?: number;
    readonly voided?: boolean;
}

export interface CloverRefundResponseBody {
    readonly payment?: CloverRefundPaymentBody;
    readonly paymentId: string;
    readonly refund?: CloverRefundBody;
}

export interface CloverRefundErrorBody {
    readonly message: string;
    readonly type: string;
    readonly code?: string;
    readonly requestId?: string;
    readonly requestType?: string;
}

export interface CloverCloudPayRefundSuccessfulResponseDetails {
    readonly kind: typeof CloverRefundKind.CLOUD_PAY;
    readonly outcome: typeof CloverRefundOutcome.REFUNDED;
    readonly httpStatus: 200;
    readonly body: CloverRefundResponseBody;
}

export interface CloverEcommerceRefundResponseBody {
    readonly id: string;
    readonly object: 'refund';
    readonly amount: number;
    readonly charge: string;
    readonly created: number;
    readonly status: 'succeeded';
    readonly metadata?: {
        readonly refundType?: string;
        readonly authCode?: string;
        readonly refNum?: string;
        readonly gatewayResponseCode?: string;
    };
}

export interface CloverEcommerceRefundSuccessfulResponseDetails {
    readonly kind: typeof CloverRefundKind.ECOMMERCE;
    readonly outcome: typeof CloverRefundOutcome.REFUNDED;
    readonly httpStatus: 200;
    readonly body: CloverEcommerceRefundResponseBody;
}

export type CloverRefundSuccessfulResponseDetails =
    | CloverCloudPayRefundSuccessfulResponseDetails
    | CloverEcommerceRefundSuccessfulResponseDetails;

export interface CloverRefundRejectedResponseDetails {
    readonly kind: CloverRefundKind;
    readonly outcome: typeof CloverRefundOutcome.REJECTED;
    readonly httpStatus: number;
    readonly body: CloverRefundErrorBody;
}

export interface CloverRefundMalformedResponseDetails {
    readonly kind: CloverRefundKind;
    readonly outcome: typeof CloverRefundOutcome.MALFORMED_RESPONSE;
    readonly httpStatus: number;
    readonly body: unknown;
    readonly message: string;
}

// Discriminator: outcome
export type CloverRefundResponseDetails =
    | CloverRefundSuccessfulResponseDetails
    | CloverRefundRejectedResponseDetails
    | CloverRefundMalformedResponseDetails;

export const CloverVoidOutcome = {
    VOIDED: 'voided',
    REFUNDED: 'refunded',
    REJECTED: 'rejected',
    MALFORMED_RESPONSE: 'malformed_response',
} as const;

export type CloverVoidOutcome =
    (typeof CloverVoidOutcome)[keyof typeof CloverVoidOutcome];

export const CloverVoidStatus = {
    SENT_TO_SERVER: 'SENT_TO_SERVER',
    IN_DEVICE_QUEUE: 'IN_DEVICE_QUEUE',
} as const;

export type CloverVoidStatus =
    (typeof CloverVoidStatus)[keyof typeof CloverVoidStatus];

export const CloverVoidPaymentResult = {
    SUCCESS: 'SUCCESS',
    FAIL: 'FAIL',
    INITIATED: 'INITIATED',
    VOIDED: 'VOIDED',
    VOIDING: 'VOIDING',
    VOID_FAILED: 'VOID_FAILED',
    AUTH: 'AUTH',
    AUTH_COMPLETED: 'AUTH_COMPLETED',
    DISCOUNT: 'DISCOUNT',
} as const;

export type CloverVoidPaymentResult =
    (typeof CloverVoidPaymentResult)[keyof typeof CloverVoidPaymentResult];

export interface CloverVoidReferenceBody {
    readonly id?: string;
}

export interface CloverVoidCardTransactionBody {
    readonly authCode?: string;
    readonly referenceId?: string;
    readonly state?: string;
    readonly token?: string;
    readonly transactionNo?: string;
    readonly type?: string;
}

export interface CloverVoidPaymentBody {
    readonly id?: string;
    readonly amount?: number;
    readonly result?: CloverVoidPaymentResult;
    readonly cardTransaction?: CloverVoidCardTransactionBody;
    readonly externalPaymentId?: string;
    readonly externalReferenceId?: string;
    readonly refunds?: readonly unknown[];
    readonly voidPaymentRef?: CloverVoidReferenceBody;
    readonly voidReason?: CloverVoidReason;
}

export interface CloverCloudPayVoidResponseBody {
    readonly payment?: CloverVoidPaymentBody;
    readonly paymentId: string;
    readonly voidReason?: CloverVoidReason;
    readonly voidStatus?: CloverVoidStatus;
}

export interface CloverEcommerceVoidResponseBody {
    readonly id: string;
    readonly object: 'refund';
    readonly amount: number;
    readonly charge: string;
    readonly created?: number;
    readonly status: 'succeeded';
    readonly metadata?: {
        readonly refundType?: string;
        readonly authCode?: string;
        readonly refNum?: string;
        readonly gatewayResponseCode?: string;
    };
}

export type CloverVoidResponseBody =
    | CloverCloudPayVoidResponseBody
    | CloverEcommerceVoidResponseBody;

export interface CloverVoidErrorBody {
    readonly message: string;
    readonly type: string;
    readonly code?: string;
    readonly requestId?: string;
    readonly requestType?: string;
}

export interface CloverVoidSuccessfulResponseDetails {
    readonly outcome:
        | typeof CloverVoidOutcome.VOIDED
        | typeof CloverVoidOutcome.REFUNDED;
    readonly httpStatus: 200;
    readonly body: CloverVoidResponseBody;
}

export interface CloverVoidRejectedResponseDetails {
    readonly outcome: typeof CloverVoidOutcome.REJECTED;
    readonly httpStatus: number;
    readonly body: CloverVoidErrorBody;
}

export interface CloverVoidMalformedResponseDetails {
    readonly outcome: typeof CloverVoidOutcome.MALFORMED_RESPONSE;
    readonly httpStatus: number;
    readonly body: unknown;
    readonly message: string;
}

// Discriminator: outcome
export type CloverVoidResponseDetails =
    | CloverVoidSuccessfulResponseDetails
    | CloverVoidRejectedResponseDetails
    | CloverVoidMalformedResponseDetails;

export const GatewayLifecycleResponseType = {
    CARDPOINTE_AUTH: 'cardpointe_auth_response_v1',
    CARDPOINTE_AUTH_HTTP_ERROR: 'cardpointe_auth_http_error_response_v1',
    CARDPOINTE_AUTH_INVALID_JSON: 'cardpointe_auth_invalid_json_response_v1',
    CARDPOINTE_FUNDING: 'cardpointe_funding_response_v1',
    CARDPOINTE_INQUIRE: 'cardpointe_inquire_response_v1',
    CARDPOINTE_INQUIRE_BY_ORDER_ID: 'cardpointe_inquire_by_orderid_response_v1',
    CARDPOINTE_LIFECYCLE_FINALIZATION: 'cardpointe_lifecycle_finalization_response_v1',
    CARDPOINTE_LIFECYCLE_PREFLIGHT: 'cardpointe_lifecycle_preflight_response_v1',
    CARDPOINTE_REFUND: 'cardpointe_refund_response_v1',
    CARDPOINTE_TERMINAL_SALE: 'cardpointe_terminal_sale_response_v1',
    CARDPOINTE_VOID: 'cardpointe_void_response_v1',
    CLOVER_CHARGE: 'clover_charge_response_v1',
    CLOVER_CHARGE_HTTP_ERROR: 'clover_charge_http_error_response_v1',
    CLOVER_CHARGE_INVALID_JSON: 'clover_charge_invalid_json_response_v1',
    CLOVER_LIFECYCLE_FINALIZATION: 'clover_lifecycle_finalization_response_v1',
    CLOVER_LIFECYCLE_PREFLIGHT: 'clover_lifecycle_preflight_response_v1',
    CLOVER_PAYMENT: 'clover_payment_response_v1',
    CLOVER_PAYMENT_EXTERNAL: 'clover_payment_external_response_v1',
    CLOVER_PAYMENT_EXTERNAL_HTTP_ERROR: 'clover_payment_external_http_error_response_v1',
    CLOVER_WEBHOOK_PAYMENT_RETRIEVAL: 'clover_webhook_payment_retrieval_response_v1',
    CLOVER_REFUND: 'clover_refund_response_v1',
    CLOVER_TERMINAL_SALE: 'clover_terminal_sale_response_v1',
    CLOVER_VOID: 'clover_void_response_v1',
    GATEWAY_TERMINAL_SALE: 'gateway_terminal_sale_response',
    VALOR_RECONCILE: 'valor_reconcile_response_v1',
    VALOR_REFUND: 'valor_refund_response_v1',
    VALOR_SALE_COPY: 'valor_sale_copy_response_v1',
    VALOR_SALE_COPY_HTTP_ERROR: 'valor_sale_copy_http_error_response_v1',
    VALOR_SALE_COPY_INVALID_JSON: 'valor_sale_copy_invalid_json_response_v1',
    VALOR_TRANSACTION_LIST: 'valor_transaction_list_response_v1',
    VALOR_TRANSACTION_STATUS: 'valor_transaction_status_response_v1',
    VALOR_TXN_FETCH: 'valor_txnfetch_response_v1',
    VALOR_VC_PUBLISH: 'valor_vc_publish_response_v1',
    VALOR_VC_STATUS: 'valor_vc_status_response_v1',
    VALOR_VOID: 'valor_void_response_v1',
} as const;

export type GatewayLifecycleResponseType =
    (typeof GatewayLifecycleResponseType)[keyof typeof GatewayLifecycleResponseType];

export const CloverLifecyclePreflightOutcome = {
    RECORDED: 'recorded',
    REJECTED: 'rejected',
    MALFORMED_INPUT: 'malformed_input',
} as const;

export type CloverLifecyclePreflightOutcome =
    (typeof CloverLifecyclePreflightOutcome)[keyof typeof CloverLifecyclePreflightOutcome];

export interface CloverLifecyclePreflightNormalizedResult {
    readonly gateway_operation: typeof CloverLifecyclePreflightGatewayOperation.TERMINAL_SALE;
    readonly device_id: string;
    readonly amount: number;
    readonly external_payment_id: string;
    readonly final: true;
    readonly capture?: true;
    readonly external_reference_id?: string;
    readonly tip_amount?: number;
}

export type CloverLifecyclePreflightResponseDetails =
    | {
          readonly outcome: typeof CloverLifecyclePreflightOutcome.RECORDED;
          readonly normalized_result: CloverLifecyclePreflightNormalizedResult;
      }
    | {
          readonly outcome: typeof CloverLifecyclePreflightOutcome.REJECTED;
          readonly message: string;
      }
    | {
          readonly outcome: typeof CloverLifecyclePreflightOutcome.MALFORMED_INPUT;
          readonly message: string;
          readonly body: unknown;
      };

export interface CloverLifecyclePreflightResponsePayload {
    readonly gateway_lifecycle_payload_type: typeof GatewayLifecycleResponseType.CLOVER_LIFECYCLE_PREFLIGHT;
    readonly response_details: CloverLifecyclePreflightResponseDetails;
}

export const CloverLifecycleFinalizationOutcome = {
    FINALIZED: 'finalized',
    MALFORMED_RESULT: 'malformed_result',
} as const;

export type CloverLifecycleFinalizationOutcome =
    (typeof CloverLifecycleFinalizationOutcome)[keyof typeof CloverLifecycleFinalizationOutcome];

export interface CloverLifecycleFinalizationNormalizedResult {
    readonly gateway_operation: typeof CloverLifecycleFinalizationGatewayOperation.TERMINAL_SALE;
    readonly status: PaymentGatewayTerminalSaleResult['status'];
    readonly gateway_transaction_id?: string;
    readonly approved_amount?: string;
    readonly authorization_code?: string;
    readonly gateway_token?: string;
    readonly message?: string;
    readonly response_code?: string;
}

export type CloverLifecycleFinalizationResponseDetails =
    | {
          readonly outcome: typeof CloverLifecycleFinalizationOutcome.FINALIZED;
          readonly normalized_result: CloverLifecycleFinalizationNormalizedResult;
      }
    | {
          readonly outcome: typeof CloverLifecycleFinalizationOutcome.MALFORMED_RESULT;
          readonly message: string;
          readonly body: unknown;
      };

export interface CloverLifecycleFinalizationResponsePayload {
    readonly gateway_lifecycle_payload_type: typeof GatewayLifecycleResponseType.CLOVER_LIFECYCLE_FINALIZATION;
    readonly response_details: CloverLifecycleFinalizationResponseDetails;
}

export interface ProviderGatewayResponseSnapshot extends JsonObject {}

export interface CloverWebhookPaymentRetrievalResponseSnapshot extends JsonObject {
    readonly id: string;
}

export interface ValorCardMetadataSnapshot extends JsonObject {
    readonly CARD_BIN?: string;
    readonly CARD_TYPE?: string;
    readonly ISSUING_BANK?: string;
    readonly COUNTRY?: string;
    readonly CARD_LEVEL?: string;
    readonly CARD_CATEGORY?: string;
}

export interface ValorTaxComponentSnapshot extends JsonObject {
    readonly rate: number;
    readonly amount: number | string;
    readonly taxableSales: number | string;
}

export interface ValorTaxDetailsSnapshot extends JsonObject {
    readonly stateTax?: ValorTaxComponentSnapshot;
    readonly cityTax?: ValorTaxComponentSnapshot;
    readonly reducedTax?: ValorTaxComponentSnapshot;
}

export interface ValorTxnFetchRecordSnapshot extends JsonObject {
    readonly REF_TXN_ID?: string;
    readonly TXN_ID?: string;
    readonly BASE_AMOUNT?: string;
    readonly NET_AMOUNT?: string;
    readonly CUSTOM_FEE_AMOUNT?: string;
    readonly TOTAL_TAX?: string | number;
    readonly IS_VOID?: string;
    readonly IS_SETTLED?: string;
    readonly REFUND_AMOUNT?: string;
    readonly REFUND_COUNT?: string;
    readonly CARD_SCHEME?: string;
    readonly PAN?: string;
    readonly CARDHOLDER_NAME?: string;
    readonly AUTH_CODE?: string;
    readonly APPROVAL_CODE?: string;
    readonly RRN?: string;
    readonly STAN_NO?: string;
    readonly BATCH_NO?: string;
    readonly CARD_TYPE?: string;
    readonly CARD_METADATA?: ValorCardMetadataSnapshot;
    readonly TAX_DETAILS?: ValorTaxDetailsSnapshot;
}

export interface ValorTxnFetchGatewayResponseSnapshot extends JsonObject {
    readonly data: ValorTxnFetchRecordSnapshot[];
    readonly error_no?: string;
    readonly status?: string;
    readonly number_of_records?: number;
    readonly meta?: JsonObject;
}

export interface ValorVcStatusGatewayResponseSnapshot extends JsonObject {
    readonly response: {
        readonly REQ_TXN_ID?: string;
        readonly STATE?: string;
        readonly EPI?: string;
        readonly SERIAL_NO?: string;
        readonly STAN_ID?: string;
        readonly INVOICENUMBER?: string;
        readonly INITIATOR_NAME?: string;
        readonly AMOUNT?: string;
        readonly PARTIAL?: string;
        readonly TOTAL_AMOUNT?: string;
        readonly ISSUER?: string;
        readonly TRAN_TYPE?: string;
        readonly MASKED_PAN?: string;
        readonly RRN?: string;
        readonly CODE?: string;
        readonly BATCH_NO?: string;
        readonly CARDHOLDER_NAME?: string;
        readonly TXN_ID?: string;
        readonly TRAN_METHOD?: string;
        readonly [key: string]: JsonValue | undefined;
    };
    readonly error_no?: string;
}

export type GatewayLifecycleResponseJsonBody =
    | ProviderGatewayResponseSnapshot
    | ValorTxnFetchGatewayResponseSnapshot
    | ValorVcStatusGatewayResponseSnapshot
    | CloverCnpTokenSaleChargeBody
    | CloverLifecyclePreflightResponsePayload
    | CloverLifecycleFinalizationResponsePayload
    | CloverTransactionDetailsPaymentBody
    | CloverRefundResponseDetails
    | CloverTerminalSaleResponseLifecyclePayload
    | CloverVoidResponseDetails
    | CloverWebhookPaymentRetrievalResponseSnapshot;

export type GatewayLifecycleResponseVariant<
    TResponseType extends GatewayLifecycleResponseType,
    TGatewayResponseJson extends GatewayLifecycleResponseJsonBody | null,
> = {
    readonly gateway_response_type: TResponseType;
    readonly gateway_response_json: TGatewayResponseJson;
};

export type GatewayLifecycleResponse =
    | GatewayLifecycleResponseVariant<
          typeof GatewayLifecycleResponseType.VALOR_TXN_FETCH,
          ValorTxnFetchGatewayResponseSnapshot
      >
    | GatewayLifecycleResponseVariant<
          typeof GatewayLifecycleResponseType.VALOR_VC_STATUS,
          ValorVcStatusGatewayResponseSnapshot
      >
    | GatewayLifecycleResponseVariant<
          typeof GatewayLifecycleResponseType.VALOR_SALE_COPY_INVALID_JSON,
          null
      >
    | GatewayLifecycleResponseVariant<
          typeof GatewayLifecycleResponseType.CLOVER_CHARGE_INVALID_JSON,
          null
      >
    | GatewayLifecycleResponseVariant<
          typeof GatewayLifecycleResponseType.CLOVER_CHARGE,
          CloverCnpTokenSaleChargeBody
      >
    | GatewayLifecycleResponseVariant<
          typeof GatewayLifecycleResponseType.CLOVER_LIFECYCLE_PREFLIGHT,
          CloverLifecyclePreflightResponsePayload
      >
    | GatewayLifecycleResponseVariant<
          typeof GatewayLifecycleResponseType.CLOVER_LIFECYCLE_FINALIZATION,
          CloverLifecycleFinalizationResponsePayload
      >
    | GatewayLifecycleResponseVariant<
          typeof GatewayLifecycleResponseType.CLOVER_PAYMENT,
          CloverTransactionDetailsPaymentBody
      >
    | GatewayLifecycleResponseVariant<
          typeof GatewayLifecycleResponseType.CLOVER_PAYMENT_EXTERNAL,
          CloverTransactionDetailsPaymentBody
      >
    | GatewayLifecycleResponseVariant<
          typeof GatewayLifecycleResponseType.CLOVER_REFUND,
          CloverRefundResponseDetails
      >
    | GatewayLifecycleResponseVariant<
          typeof GatewayLifecycleResponseType.CLOVER_TERMINAL_SALE,
          CloverTerminalSaleResponseLifecyclePayload
      >
    | GatewayLifecycleResponseVariant<
          typeof GatewayLifecycleResponseType.CLOVER_VOID,
          CloverVoidResponseDetails
      >
    | GatewayLifecycleResponseVariant<
          typeof GatewayLifecycleResponseType.CLOVER_WEBHOOK_PAYMENT_RETRIEVAL,
          CloverWebhookPaymentRetrievalResponseSnapshot
      >
    | GatewayLifecycleResponseVariant<
          Exclude<
              GatewayLifecycleResponseType,
              | typeof GatewayLifecycleResponseType.VALOR_TXN_FETCH
              | typeof GatewayLifecycleResponseType.VALOR_VC_STATUS
              | typeof GatewayLifecycleResponseType.VALOR_SALE_COPY_INVALID_JSON
              | typeof GatewayLifecycleResponseType.CLOVER_CHARGE_INVALID_JSON
              | typeof GatewayLifecycleResponseType.CLOVER_CHARGE
              | typeof GatewayLifecycleResponseType.CLOVER_LIFECYCLE_PREFLIGHT
              | typeof GatewayLifecycleResponseType.CLOVER_LIFECYCLE_FINALIZATION
              | typeof GatewayLifecycleResponseType.CLOVER_PAYMENT
              | typeof GatewayLifecycleResponseType.CLOVER_PAYMENT_EXTERNAL
              | typeof GatewayLifecycleResponseType.CLOVER_REFUND
              | typeof GatewayLifecycleResponseType.CLOVER_TERMINAL_SALE
              | typeof GatewayLifecycleResponseType.CLOVER_VOID
              | typeof GatewayLifecycleResponseType.CLOVER_WEBHOOK_PAYMENT_RETRIEVAL
          >,
          ProviderGatewayResponseSnapshot
      >;
